import React, { useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import { InputControl } from '@si-armes/dsfr-react';
import { connect } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { getAPI, reinitialiseAPI } from 'store/apiReducer';
import usagerClientReducer from '../../../../store/usagerReducer';
import {
    API_GET_REFERENTIEL_COMMUNES,
    CONTEXT_GET_REFERENTIEL_COMMUNES,
} from '../../../../constants/apiUrls.constants';
import { NUMERO_FRANCE } from '../../../../constants/select.constants';
import ChampsAutocomplete from '../../ChampAutocomplete/ChampsAutocomplete';
import {
    getMessageErreurChampManquant,
    getPatternHookFormValidation,
} from '../../../../utils/utils';
import { REGEXP_LIEU } from '../../../../constants/contraintes.constants';

const SelectCommune = ({
    referentielCommune,
    getApi,
    reinitialiseApi,
    valueRecherche,
    setValueRecherche,
}) => {
    const { setValue, getValues, watch, trigger } = useFormContext();
    const [timeoutAppel, setTimeoutAppel] = useState();

    const getSuggestionValue = (suggestion) => {
        // Retourne le libellé brut (sans parenthèses) pour le stockage dans le formulaire
        return suggestion.libelle;
    };

    const onChange = (event, recherche) => {
        if (recherche.newValue === '') {
            setValue('lieuNaissance', '');
            setValue('codeInseeNaissance', '');
            trigger('lieuNaissance');
            reinitialiseApi(CONTEXT_GET_REFERENTIEL_COMMUNES);
        }
        setValueRecherche(recherche.newValue);
    };

    const onSuggestionsFetchRequested = (suggestion) => {
        if (getValues('lieuNaissance') === suggestion.value) {
            return;
        }
        setValue('lieuNaissance', '');
        trigger('lieuNaissance');
        if (suggestion.value.trim().length > 0) {
            if (timeoutAppel) {
                clearTimeout(timeoutAppel);
            }
            const dateNaissance = getValues('dateNaissance');
            setTimeoutAppel(
                setTimeout(() => {
                    getApi(
                        usagerClientReducer,
                        `${API_GET_REFERENTIEL_COMMUNES}?libelle=${suggestion.value}&date=${dateNaissance}`,
                        CONTEXT_GET_REFERENTIEL_COMMUNES,
                    );
                }, 1000),
            );
        }
    };

    const onSuggestionSelected = (event, suggestion) => {
        setValue('lieuNaissance', suggestion.suggestionValue);
        setValue('codeInseeNaissance', suggestion.suggestion.numInsee);
        trigger('lieuNaissance');
        reinitialiseApi(CONTEXT_GET_REFERENTIEL_COMMUNES);
    };

    const renderSuggestion = (suggestion) => {
        return (
            <span data-testid={`autocomplete-suggestion-${suggestion.numInsee}`}>
                {suggestion.libelleAvecDepartement}
            </span>
        );
    };

    const estNeApres1943 = useMemo(() => {
        const dateNaissance = watch('dateNaissance');
        if (!dateNaissance || dateNaissance.length < 4) {
            return true;
        }
        const annee = dateNaissance.slice(-4);
        return annee >= '1943';
    }, [watch('dateNaissance')]);

    const isDisabled = useMemo(() => {
        const dateNaissance = getValues('dateNaissance');
        const dateComplete = dateNaissance && dateNaissance.length === 10;
        return getValues('paysNaissance') === '' || !dateComplete;
    }, [watch('dateNaissance'), watch('paysNaissance')]);

    return (
        <Controller
            render={({ field, fieldState }) => (
                <>
                    {getValues('paysNaissance') === NUMERO_FRANCE &&
                    estNeApres1943 &&
                    !isDisabled ? (
                        <ChampsAutocomplete
                            label="Lieu de naissance"
                            suggestions={referentielCommune}
                            getSuggestionValue={getSuggestionValue}
                            inputProps={{
                                name: 'commune',
                                onChange,
                                value: getValues('lieuNaissance') || valueRecherche,
                                required: true,
                            }}
                            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                            onSuggestionSelected={onSuggestionSelected}
                            renderSuggestion={renderSuggestion}
                            afficherRequired
                            error={fieldState.error}
                            description="Recherchez le lieu de naissance en saisissant au moins 3 lettres, puis sélectionnez le dans la liste"
                        />
                    ) : (
                        <InputControl
                            autocomplete="address-line3"
                            id="lieuNaissance"
                            label="Lieu de naissance *"
                            required
                            uppercase
                            error={fieldState.error}
                            {...field}
                            disabled={isDisabled}
                        />
                    )}
                </>
            )}
            name="lieuNaissance"
            rules={{
                required: getMessageErreurChampManquant('lieu de naissance'),
                pattern: getPatternHookFormValidation(REGEXP_LIEU, 'lieu de naissance'),
            }}
        />
    );
};

SelectCommune.propTypes = {
    referentielCommune: PropTypes.array,
    getApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.shape({
        message: PropTypes.string.isRequired,
    }),
    warning: PropTypes.shape({
        message: PropTypes.string.isRequired,
    }),
    valid: PropTypes.shape({
        message: PropTypes.string.isRequired,
    }),
    valueRecherche: PropTypes.string.isRequired,
    setValueRecherche: PropTypes.func.isRequired,
};

SelectCommune.defaultProps = {
    isLoading: null,
    referentielCommune: null,
};

export default connect(
    (state) => {
        const { erreur, data, message } = state.referentiel.communes;
        return {
            erreur,
            referentielCommune: data,
            message,
        };
    },
    {
        getApi: getAPI,
        reinitialiseApi: reinitialiseAPI,
    },
)(SelectCommune);
